import { zodResolver } from "@hookform/resolvers/zod";
import { Info, Loader } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCreateSKUMutation } from "@/data/mutations";

const createSKUFormSchema = z.object({
  name: z.string().trim().min(1).max(75),
  notes: z.string().optional(),
});

export const CreateSKU = () => {
  const form = useForm<z.infer<typeof createSKUFormSchema>>({
    resolver: zodResolver(createSKUFormSchema),
    defaultValues: {
      name: "",
      notes: "",
    },
  });
  const createSKUMutation = useCreateSKUMutation();

  const handleSubmit = (values: z.infer<typeof createSKUFormSchema>) => {
    createSKUMutation.mutate(values, {
      onError: (error) => {
        const serverErrors = (
          error as {
            response?: { data?: { errors?: Record<string, string[]> } };
          }
        )?.response?.data?.errors;

        if (serverErrors) {
          Object.keys(serverErrors).forEach((fieldName) => {
            if (fieldName === "name" || fieldName === "notes") {
              form.setError(fieldName, {
                type: "server",
                message: serverErrors[fieldName].join(", "),
              });
            }
          });
        }
      },
    });
  };

  return (
    <div>
      <h2 className="scroll-m-20 text-3xl font-medium tracking-tight first:mt-0">
        Create SKU
      </h2>

      <Alert className="mt-6">
        <Info className="h-4 w-4" />
        <AlertTitle className="text-base">What is a SKU?</AlertTitle>
        <AlertDescription className="text-sm">
          A SKU is a collection of items and stands for Stock Keeping Unit. Each
          SKU is given a number for easy identification.
        </AlertDescription>
      </Alert>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Spring Trade Show" {...field} />
                </FormControl>
                <FormDescription>
                  This is the display name of the SKU and its associated Digital
                  Product. Max 75 characters.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Input placeholder="Our project with Acme Corp" {...field} />
                </FormControl>
                <FormDescription>
                  Use this to add any additional notes about this SKU. This will
                  not be displayed publicly.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-2 mt-6">
            {!createSKUMutation.isPending ? (
              <Button variant="secondary" asChild>
                <Link to="/skus">Cancel</Link>
              </Button>
            ) : (
              <Button variant="secondary" disabled>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={!form.formState.isValid || createSKUMutation.isPending}
            >
              {createSKUMutation.isPending ? (
                <Loader className="h-3 w-3 animate-spin" />
              ) : null}
              Create
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
